// shared/src/resolvers/llm-datetime-coerce.ts
// 2026-06-11 — normalize LLM-emitted DATETIME strings to canonical ISO-8601 Z
// at the boundary, instead of hard-failing the model against a rigid
// z.string().datetime() (strict RFC-3339).
//
// Output-side sibling of llm-number-coerce.ts / llm-string-coerce.ts. The
// p-arbiter `event_horizon_at: z.string().datetime()` was the single
// highest-risk non-enum brief-killer (audit 2026-06-11,
// docs/investigations/llm-schema-rigidity-audit-pticker-2026-06-11.md): strict
// RFC-3339 rejects the space-form (`2026-06-07 15:00:00`), missing-seconds
// (`2026-06-07T15:00`), missing-Z (`2026-06-07T15:00:00`), `+00:00`-offset, and
// millisecond shapes DeepSeek routinely produces — on the most expensive
// (Pro-tier) per-ticker stage.
//
// CONSERVATIVE BY DESIGN (per [[feedback_no_fallbacks]]): we NORMALIZE the
// recoverable drift forms to canonical `…Z` (so downstream — the ISO-Z
// signals.event_horizon_at column + its strftime cutoff comparisons, per the
// CLAUDE.md C018 rule — keeps getting canonical ISO). A genuinely unparseable
// datetime passes through UNCHANGED so the wrapping z.string().datetime() still
// fail-louds (a garbage horizon is material). We never invent a date.
//
// Twin-of-narrow note: shared/src/schemas/signal.ts has a NARROW coerceDatetime
// (space-form only) for read-side SQLite-backfill rows. THIS coercer is the
// wider write-side gate at the arbiter's OutputSchema (the FIRST validator the
// model output hits, before signal.ts ever sees it).

import { z } from 'zod';

// date · [ T| ]time(HH:MM[:SS[.fff]]) · optional zone (Z | ±HH:MM | ±HHMM)
const DATETIME_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)(Z|z|[+-]\d{2}:?\d{2})?$/;

/**
 * Normalize one LLM datetime to canonical ISO-8601 Z, or pass it through for the
 * wrapping z.string().datetime() to fail-loud on. Handles: space separator,
 * missing seconds, missing zone (assumed UTC), zero-offset (`+00:00`→`Z`), and
 * non-zero numeric offsets (converted to UTC via Date — unambiguous because the
 * offset is explicit; the timezone-trap only affects offset-LESS strings, which
 * we normalize by string-append, never Date-parse). Already-canonical strings
 * are idempotent. Non-string / unrecognized values pass through unchanged.
 */
export function coerceLlmDatetime(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  let s = v.trim();
  if (s === '') return v;
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }

  const m = DATETIME_RE.exec(s);
  if (m && m[1] && m[2]) {
    const date = m[1];
    let time = m[2];
    const zoneRaw = m[3];
    if (/^\d{2}:\d{2}$/.test(time)) time += ':00';            // ensure seconds

    // offset-less, Z, or zero-offset → canonical Z by pure string-append (no
    // Date parse → no local-timezone trap)
    if (!zoneRaw || /^[zZ]$/.test(zoneRaw) || /^[+-]00:?00$/.test(zoneRaw)) {
      return `${date}T${time}Z`;
    }
    // explicit non-zero numeric offset → unambiguous; convert to UTC
    const d = new Date(`${date}T${time}${zoneRaw}`);
    return Number.isNaN(d.getTime()) ? v : d.toISOString();
  }

  // date-only → midnight UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00Z`;

  return v;   // unrecognized → wrapping z.string().datetime() fail-louds
}

/**
 * Drop-in replacement for `z.string().datetime()` on LLM-emitted datetime fields.
 * Recovers the common drift forms (→ canonical `…Z`) before the strict
 * RFC-3339 check, which STAYS so downstream consumers still receive canonical
 * ISO-Z. z.infer is `string`, so typed consumers are unchanged.
 */
export const llmDatetime = z.preprocess(coerceLlmDatetime, z.string().datetime());
